import { ApiError } from "@/lib/api-client";
import { formatDate } from "@/lib/format-date";
import { formatWaitMinutes, readRetryAfterSeconds } from "@/lib/retry-after";
import {
  TARGET_BLOCKED_REASON,
  TARGET_BLOCKED_TITLE,
} from "@/routes/org-admin/member-actions-error";

// Story 11.8 AC11 + 11.9 AC6 — the ONE mapper every refusal in this unit reaches
// the user through. Follows the shipped `invitation-error.ts` convention exactly:
// branch on `ApiError.code` / `ApiError.status` / a 422 `loc` / a structured
// `details.reason` — NEVER on the server's raw `msg` (`invitation-error.ts:7-22`),
// and order the branches so a specific code wins over a bare status.
//
// TWO RULES THIS SURFACE ADDS, both from findings routed into this story:
//
// 1. RENDER THE CODE THAT ARRIVES, never a hardcoded assumption about what it
//    means. `ADMIN-009` is double-claimed by 11.2 and 11.12 and is being resolved
//    at grooming, so no `ADMIN-009 → meaning` entry ships (boundary #10); it falls
//    to the default branch, which names the server's own code and nothing else.
//
// 2. NEVER SURFACE A 409's STRING `details`. Four of the reachable conflicts
//    (`UserAlreadyBlockedException` and `LastAdminException` in
//    `application/common/exceptions.py`; `UserAlreadyUnblockedException` and
//    `UserNotBlockedException` in `authorization/unblock_user/exceptions.py`)
//    carry `error_code: null` and a BARE STRING `details`. Until Story 16.9 those
//    strings also embedded the target's EMAIL ADDRESS; they no longer do, and the
//    negative that keeps it that way is
//    `test_the_shared_conflict_responses_do_not_echo_the_target_email`. The rule
//    below is UNCHANGED by that, because disclosure was only ever half its reason:
//    the four remain genuinely indistinguishable from one another without matching
//    English prose, which this file is forbidden to do — so they collapse to ONE
//    conflict banner that tells the admin the row is stale and to refresh. That
//    is the correct action for every one of them except `LastAdminException`,
//    which is a real refusal rather than a stale row, and it discloses
//    nothing. The backend half —
//    giving each a namespaced code and a dict `details` — is filed as a handoff;
//    when it lands, each gains its own branch here. `ADMIN-015` is the first to
//    land: the reactivate surface stopped raising `UserAlreadyUnblockedException`
//    and now answers "already active" with its own code and a dict `details`, so
//    that one has its own branch below. The collapsed family still covers the
//    shape, because the raw authorization route keeps answering code-free.

/** Banner descriptor. `variant` maps 1:1 onto a `Banner` variant. */
export interface AdminUserBanner {
  /** `info` = a settled state (no retry); `warn` = check/conflict; `danger` = refusal/transport. */
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
  /**
   * The server's own error code, rendered verbatim beside the copy on the default
   * branch. Present ONLY where the mapper could not name the meaning — it is the
   * support handle, not a translation.
   */
  code?: string;
  /** True when the honest remedy is "the row you are looking at is stale". */
  refetch?: boolean;
  /** True when the screen should offer Back to users instead of a retry (AC10). */
  backToUsers?: boolean;
}

/** Inline messages keyed to the 11.9 create-user form's RHF field names. */
export interface AdminUserFieldErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface MappedAdminUserError {
  /** Banner descriptor, or null when the failure is fully handled inline. */
  banner: AdminUserBanner | null;
  /** Per-field messages to attach via `form.setError` without dropping form state. */
  fieldErrors: AdminUserFieldErrors;
}

const GENERIC: AdminUserBanner = {
  variant: "danger",
  title: "Something went wrong",
  body: "The action could not be completed. Please try again.",
};

// 401 and 403-without-a-capability-code share ONE banner. The invite endpoint
// answers an anonymous caller with 403, not 401 (finding [→11-9] R4), so keying a
// "signed out" state off 401 alone would report an expired session as a permanent
// capability refusal — and vice versa.
const SESSION_EXPIRED: AdminUserBanner = {
  variant: "warn",
  title: "Your session may have expired",
  body: "Sign in again and retry this action.",
};

// ADMIN-006 — the actor's role ceiling. A settled account state; no retry.
const INSUFFICIENT_SCOPE: AdminUserBanner = {
  variant: "warn",
  title: "You can't take this action",
  body: "Your account isn't permitted to manage this user.",
};

// ADMIN-007 — the target is outside the actor's organization scope. The endpoint
// answers a FOREIGN id and an UNKNOWN id with the same code on purpose, so this
// copy must be the same for both: a different message for "no such user" would
// turn the UI into the existence oracle the API refuses to be (11.3 AC4, AC10).
const OUTSIDE_SCOPE: AdminUserBanner = {
  variant: "danger",
  title: "User not available",
  body: "This user isn't in your organization, or doesn't exist. Return to the user list and search again.",
  backToUsers: true,
};

// ADMIN-012 — the target has never activated, so there is no credential to reset.
const TARGET_NOT_ACTIVATED: AdminUserBanner = {
  variant: "warn",
  title: "This user hasn't activated their account yet",
  body: "There's no password to reset until they complete activation. Send them their invitation again instead.",
};

// ORG-017 — CITS has blocked the target's platform access, so the org_admin's
// password reset is refused (P0-ORG-17, Story 15.6 AC17/AC18). Only an org_admin
// can ever SEE this: `administration/reset_user_password` leaves a super_admin
// deliberately unfenced, so the copy can speak to the one actor who reaches it.
//
// `info`, not `warn`: nothing went wrong and nothing needs re-checking. It is a
// settled fact about the TARGET, and the remedy is off this screen entirely.
//
// The copy is IMPORTED from the org-admin mapper rather than restated. Both
// surfaces refuse the same action on the same target for the same reason, and an
// admin who hits it on one screen and then the other must not be handed two
// different accounts of one situation.
const TARGET_BLOCKED: AdminUserBanner = {
  variant: "info",
  title: TARGET_BLOCKED_TITLE,
  body: TARGET_BLOCKED_REASON,
};

// ADMIN-014 — reactivate against a target that is inactive but was never BLOCKED.
// Not an ordinary failed action: the person was never activated, so the correct
// affordance is invite / resend credentials, and the copy has to say so.
const TARGET_NOT_BLOCKED: AdminUserBanner = {
  variant: "warn",
  title: "This user isn't blocked",
  body: "Their access was never blocked, so there's nothing to reactivate — they haven't activated their account. Use Change Password to resend their credentials.",
};

// ADMIN-015 — reactivate against a target that was never blocked and already has
// access. The account is fine and the screen is out of date, so the honest copy
// names the no-op rather than sending the admin to fix a state that isn't broken.
// Deliberately NOT ADMIN-014's copy: an administrator looking at a perfectly
// healthy account must not be handed the never-activated sentence.
const TARGET_ALREADY_ACTIVE: AdminUserBanner = {
  variant: "warn",
  title: "This user already has access",
  body: "Their access was never blocked and the account is already active, so reactivating it would change nothing.",
};

// ADMIN-017 (403) — enable-paid-account against a target with no LIVE completed
// checkout. This refusal IS the control: without it the action would be "a
// super_admin may activate any account", which is a way to hand out platform
// access with no payment on the most privileged screen there is. So the copy
// says what the action does and does NOT do — an admin who reads "no completed
// checkout" and reaches for a different button is the failure — and it names the
// door that DOES apply, because an account that should have access without
// paying is a manual entitlement override, not this.
//
// `info` rather than `danger`: nothing went wrong. The admin asked a reasonable
// question and the answer is a settled fact about the target.
const NO_COMPLETED_CHECKOUT: AdminUserBanner = {
  variant: "info",
  title: "This account hasn't completed checkout",
  body: "There's no live payment on this account, so it can't be enabled here — this action only finishes an activation that a payment already paid for. Grant an entitlement override if this account should have access without paying.",
};

// ADMIN-018 (409) — enable-paid-account against a BLOCKED target. A block is a
// withdrawal of trust and Reactivate is the only door back; a payment does not
// lift one. Deliberately neither folded into "already active" nor answered as a
// silent no-op, either of which would quietly re-enable a blocked clinician
// through the billing surface — the one outcome the block exists to prevent.
const TARGET_BLOCKED_NOT_PAYABLE: AdminUserBanner = {
  variant: "warn",
  title: "This account is blocked",
  body: "A completed payment doesn't lift a block. Reactivate the account first, then enable it.",
};

// ---------------------------------------------------------------------------
// Story 22.1 (FR88, AC24) — the attach refusals.
//
// Every one of them is answered HERE rather than left to a status branch,
// because every status branch available to them gives advice that is FALSE. All
// six of the new codes are 409s, and the 409 fall-through is STALE_CONFLICT —
// "someone else may have updated this account, refresh" — which is wrong about
// each of them and which the administrator can follow forever: a full
// organization does not empty on a refresh, a lapsed contract does not renew on
// one, and an erased account never comes back.
//
// The copy names the DOOR, not just the refusal. An admin who reads "no seats
// available" and does not learn that raising capacity is the remedy goes looking
// for a different button, and the one they find is the wrong one.
// ---------------------------------------------------------------------------

// ORG-023 — the organization has no seat left to give.
const ORG_NO_REMAINING_SEATS: AdminUserBanner = {
  variant: "warn",
  title: "That organization has no seats available",
  body: "Every seat it has bought is already assigned. Raise its seat capacity on the organization's page, then attach this clinician.",
};

// ORG-024 — the contract is not in force. THREE reasons, three sentences: the
// remedy for a lapsed contract (renew) is not the remedy for one that has not
// started (wait), and neither is the remedy for missing dates (record them). A
// single collapsed sentence would send two administrators in three to the wrong
// place, which is the whole reason the server sends a structured `reason`.
function contractNotInForceBanner(details: unknown): AdminUserBanner {
  const reason = readReason(details);
  switch (reason) {
    case "contract_lapsed":
      return {
        variant: "warn",
        title: "That organization's contract has ended",
        body: "A clinician cannot adopt a contract term that is already over. Renew the organization's contract, then attach them.",
      };
    case "contract_not_started":
      return {
        variant: "warn",
        title: "That organization's contract hasn't started yet",
        body: "There is nothing for this clinician to adopt until the term begins. Attach them on or after the contract start date.",
      };
    case "contract_dates_missing":
      return {
        variant: "warn",
        title: "That organization has no contract dates",
        body: "Without a start and an end date there is no term to place this clinician into. Record the contract dates on the organization's page first.",
      };
    default:
      // A reason this client has never heard of. Say only what is certainly
      // true and hand over the code, rather than guessing which of the three
      // it resembles and sending the admin somewhere that cannot help.
      return {
        variant: "warn",
        title: "That organization's contract isn't in force",
        body: "The server refused the attach on the organization's contract term. Check its contract dates before retrying.",
        code: "ORG-024",
      };
  }
}

// ORG-025 — a personal organization. Never a seat host: it exists so that every
// account has an organization to resolve its own token against, and it has room
// for exactly its owner. The picker does not list one, so reaching this means
// the id came from somewhere else.
const ORG_IS_PERSONAL: AdminUserBanner = {
  variant: "warn",
  title: "That's a personal organization",
  body: "A personal organization belongs to one account and never hosts other people's seats. Choose a professional organization instead.",
};

// ORG-026 — the target already holds a seat. Deliberately not a silent success:
// the admin would otherwise believe they had moved someone who never moved.
// Moving a clinician between organizations is two acts, and the first of them is
// not this one.
const TARGET_ALREADY_SEATED: AdminUserBanner = {
  variant: "warn",
  title: "This clinician already holds a seat",
  body: "They are already on an organization's roster, so there is nothing to attach. Remove them from their current seat first if they are moving.",
  refetch: true,
};

// ORG-027 — an erased account. The server's own message is deliberately
// action-neutral so that Story 22.3's reversal dialog can render the same
// sentence.
//
// Story 22.4 (from 22.3's handoff, DW-838): this banner must be action-neutral
// TOO. One branch below maps ORG-027, and 22.3 routed the reversal's refusal
// through it — so the body ran on both acts while describing only one. "There
// is no account left to place on a roster" is the attach's sentence; a reversal
// is the opposite act, TAKING someone off a roster, and an admin undoing a
// mistaken attach was told the reason was that the person could not be put on
// one. Per-action bodies were the alternative and would mean threading the act
// through a mapper that has never known which call it is answering for; one
// sentence true of both is the smaller correct change, and it is the sentence
// `ReverseAttachmentDialog` already renders for the same code.
const TARGET_ERASED: AdminUserBanner = {
  variant: "warn",
  title: "This account has been deleted",
  body: "Deletion is permanent, and a deleted account admits no administrative action.",
};

// ORG-028 — certified before the platform (issue #596 is open). Attaching would
// have the organization pay for a seat this clinician cannot use, and the
// refusal THEY would meet names a lapsed contract on a live one. Refuse here,
// where the sentence can be true.
const TARGET_ALREADY_CERTIFIED: AdminUserBanner = {
  variant: "warn",
  title: "This clinician is already certified",
  body: "Access for clinicians certified before the platform is still being decided, so they can't be given an organization seat yet. Ask CITS before promising this person access.",
};

// ORG-032 — the target has not completed calibration (DW-814, the PM ruling of
// 2026-09-22). ORG-028's TWIN, and beside it for the same reason the server
// raises them in sequence: both refuse because the seat could not be used.
//
// It must NOT fall through to the 409 branch. STALE_CONFLICT tells the admin the
// row is out of date and to refresh, and no refresh has ever completed anybody's
// calibration — verbatim the failure ORG-017's note above records, and the one
// the Story 22.1 block calls advice "the administrator can follow forever".
//
// Deliberately NOT ORG-028's copy, though the server raises them one after the
// other on the same predicate. ORG-028's reason is an OPEN product question and
// the honest next step is to ask CITS; this one names something the clinician can
// actually do, after which this very same attach succeeds. An admin handed the
// #596 sentence for a clinician who is simply mid-calibration would go and open a
// support conversation about a case that needs none.
//
// The body is the SERVER'S OWN SENTENCE, copied as a literal. That is not header
// rule 1's forbidden move: nothing is read off `error.details` here, so no date,
// name or address the server formats can ever ride in — the rule's actual hazard.
// What it buys is one account of this situation, because `calibration`'s own
// surfaces refuse the same clinician for the same reason, and an admin who reads
// the two must not be handed two. It names no date, no deadline and no
// remaining-case count on purpose: those are `calibration`'s facts to state, and
// a stale copy of one here would be a second derivation of a number this file
// cannot see.
//
// No `refetch`: the state is not stale. Calibration being incomplete is a
// standing fact about the TARGET, and it changes when they finish, not when this
// screen reloads.
const TARGET_NOT_CALIBRATED: AdminUserBanner = {
  variant: "warn",
  title: "This clinician hasn't completed calibration",
  body: "This clinician has not completed calibration yet, so a seat would grant them no access. Attach them once their calibration is complete.",
};

// ORG-033 — the target's account is not active (the PM ruling of 2026-09-22).
// The server gated the attach on `blocked_at` alone, which is only half of SCP
// §4D state 8; the other half — `is_active = false` with no block stamp — is
// exactly what Story 14.6's seat removal leaves behind, and it went through with
// a 200.
//
// It must NOT fall through to the 409 branch. STALE_CONFLICT tells the admin the
// row is out of date and to refresh, and no refresh has ever restored anybody's
// access — verbatim the failure ORG-017's and ORG-032's notes above record, on
// the third code in a row that would have met it.
//
// Deliberately NOT ORG-017's banner, even though both are "this person cannot be
// put on a roster". ORG-017's copy states that CITS blocked the account and that
// only CITS can lift it; neither half is true here, and telling an administrator
// a block happened when none did sends them to ask CITS to undo a decision
// nobody took. Deliberately not ADMIN-014's either ("Use Change Password to
// resend their credentials"), which is the never-activated invitee's affordance
// and is false for the released clinician this refusal mostly answers.
//
// The body is the SERVER'S OWN SENTENCE, copied as a literal, on ORG-032's
// argument: nothing is read off `error.details`, so no server-formatted value
// can ride in, and the operator meets one account of this situation rather than
// two. It names the ACT and never a control, because there is no single button
// that restores access — the console's own Reactivate refuses this very target
// (409 ADMIN-014 when `blocked_at` is null) — and naming one would be the DW-235
// failure this file already carries a scar from.
//
// No `refetch`: the state is not stale. The account being inactive is a standing
// fact about the TARGET, and it changes when somebody restores their access, not
// when this screen reloads.
const TARGET_INACTIVE: AdminUserBanner = {
  variant: "warn",
  title: "This clinician's account isn't active",
  body: "This clinician's account is not active, so they could not sign in to use a seat. Restoring their access is a separate decision, and it has to be made before they can be attached.",
};

// ---------------------------------------------------------------------------
// Story 22.3 (AC27) — the three reversal refusals. ORG-027 above is the fourth
// and is SHARED with the attach, which is why its sentence names neither act.
//
// All three are 409s and all three would otherwise land on STALE_CONFLICT,
// which tells the admin the screen is out of date and to reload. None of them
// clears on a reload: the first two are facts about the roster and the third is
// a deadline that has already passed.
// ---------------------------------------------------------------------------

// ORG-029 — nothing to reverse. Also the SECOND reversal of the same
// attachment: the first stamped `released_at`, so there is no longer an active
// seat to find. `refetch` because this is the one of the three where the screen
// genuinely is behind — the seat went away, here or in another tab.
const NO_ACTIVE_ATTACHMENT: AdminUserBanner = {
  variant: "warn",
  title: "There's no attachment to reverse",
  body: "This clinician doesn't hold an active seat on this organization. If the reversal has already been done, the seat is back in the pool.",
  refetch: true,
};

// ORG-030 — an INVITED seat. The organization's own administrator owns this one
// under FR83, and removing it does not give the seat back. Naming that route is
// the whole point of the banner: the admin's next move is a different screen.
const NOT_ATTACHMENT_CREATED: AdminUserBanner = {
  variant: "warn",
  title: "This seat wasn't created by an attachment",
  body: "The clinician joined through the organization's own invitation, so there is no CITS attachment to correct. The organization's administrator removes a seat from their own seat management screen.",
};

/**
 * ORG-031 — the correction window has closed. The deadline and the attachment
 * date are facts only the server holds, so the copy is BUILT from the
 * structured `window_days` / `assigned_at` fields rather than echoing the
 * server's sentence — `cooldownBanner` above, same reason.
 */
function windowElapsedBanner(details: unknown): AdminUserBanner {
  const windowDays = readDetailValue(details, "window_days");
  const assignedAt = readDetailValue(details, "assigned_at");
  const window = windowDays
    ? ` The window is ${windowDays} days from the attachment date`
    : "";
  const when = window
    ? assignedAt
      ? `${window}, and this seat was assigned on ${formatDate(assignedAt)}.`
      : `${window}.`
    : "";
  return {
    variant: "warn",
    title: "The correction window has closed",
    body: `This attachment can no longer be reversed.${when} Removing the seat now is the organization's own action, and it does not return the seat to their pool.`,
  };
}

// ORG-009 (403) and ORG-019 (409) are NOT new, but the attach can raise both and
// neither had a branch on this screen. ORG-009 would fall to the uncoded-403
// session banner and ORG-019 to STALE_CONFLICT — a sign-in prompt and a refresh
// prompt, neither of which is the situation.
const TARGET_NOT_A_CLINICIAN: AdminUserBanner = {
  variant: "warn",
  title: "This account isn't a clinician",
  body: "Only a clinician account can hold an organization's clinical seat. Administrators and platform staff are rostered a different way.",
};

const TARGET_ACCOUNT_RECORD_MISSING: AdminUserBanner = {
  variant: "danger",
  title: "This account's records are incomplete",
  body: "Part of this account is missing on the server, so it can't be placed on a roster. Report this to CITS with the account's email — retrying will not fix it.",
  code: "ORG-019",
};

// Story 20.3 (AC16) + the PR #821 review — the four FR86 calibration-deadline
// refusals. All four reach the admin through a dialog that has just taken a date
// and a reason, and all four are answered here rather than left to a status
// branch, because every status branch available to them gives ADVICE THAT IS
// FALSE:
//
//   ADMIN-019 is a 400 and would fall to the default banner's "Please try again"
//   — and trying again with the same date will be refused forever.
//   ADMIN-020, ADMIN-021 and ADMIN-022 are 409s and would land on STALE_CONFLICT,
//   which tells the admin to refresh a row that is not stale. Refreshing will
//   restate exactly the same deadline and exactly the same exemption.
//
// The copy is OURS, never the server's `details.message`. Each exception carries
// a serviceable English sentence and this file is still forbidden to render it
// (header rule 1, `invitation-error.ts:7-22`) — an embedded date the server
// formatted is still server prose, and the day it gains a name or an email the
// mapper has no way to notice.

// ADMIN-019 (400) — the date chosen is already in the past. One of the two
// refusals the admin can fix without leaving the dialog, so the copy NAMES THE
// BOUNDARY rather than describing the failure: "today or later" is the whole
// rule, and an admin who is told only "that date was refused" has to guess
// whether the limit is today, tomorrow, or the current deadline.
//
// `warn`, not `danger`: nothing is broken and the next attempt can succeed.
const CALIBRATION_DEADLINE_IN_PAST: AdminUserBanner = {
  variant: "warn",
  title: "Choose a date that hasn't passed",
  body: "A new calibration deadline must be today or later. Pick a future date and submit again.",
};

// ADMIN-022 (409) — the submitted date is EARLIER than the deadline already in
// force, so the write would SHORTEN the clinician's window under an event named
// "extended". Like ADMIN-019, this is a refusal the admin can fix without leaving
// the dialog, so the copy NAMES THE RULE rather than describing the failure — an
// admin told only "that date was refused" has just been told the same thing
// ADMIN-019 tells them, and will try another early date.
//
// It must NOT fall through to the 409 stale-conflict branch. The row is not
// stale: refreshing it restates exactly the same deadline, and the advice to
// reload is advice this admin can follow indefinitely without ever succeeding.
//
// The body points at "the deadline shown above" rather than repeating a date.
// The dialog's own sub-line already prints the deadline in force
// (`ExtendCalibrationDeadlineControl.tsx`), and the server's sentence — which
// does name both dates — is server prose this file is forbidden to render
// (header rule 1).
//
// `warn`, not `danger`: nothing is broken and the next attempt can succeed.
const CALIBRATION_DEADLINE_SHORTENED: AdminUserBanner = {
  variant: "warn",
  title: "Choose a later date",
  body: "A calibration deadline can only be moved later, never earlier. Pick a date after the deadline shown above and submit again.",
};

// ADMIN-020 (409) — the submitted date is the deadline already in force. A no-op
// is refused BEFORE it can emit, because the audit store is append-only with a
// seven-year retention and a row saying an administrator moved a deadline that
// did not move is a permanent false statement about what they did.
//
// The copy must not read as an error the admin caused: their intent already
// holds. It says so, and points at the only thing that would change anything.
const CALIBRATION_DEADLINE_UNCHANGED: AdminUserBanner = {
  variant: "info",
  title: "That's already this clinician's deadline",
  body: "The date you chose is the deadline already in force, so nothing would change. Choose a different date, or close this dialog.",
};

// ADMIN-021 (409) — the target has no deadline to move: calibration is complete,
// or they carry the `already_certified` bypass. A settled fact about the PERSON,
// not about the date, which is why it is `info` and why the copy names the two
// reasons — an admin looking at the wrong row needs to recognise that, and an
// admin looking at the right row needs to stop trying.
//
// Deliberately NOT collapsed into ADMIN-020's copy: "choose a different date"
// is advice this admin can follow indefinitely without ever succeeding.
const TARGET_HAS_NO_CALIBRATION_DEADLINE: AdminUserBanner = {
  variant: "info",
  // "This account", not "This clinician": leg 2 fires precisely on a target who
  // is NOT one, and a sentence that named them a clinician would be the one
  // place this refusal contradicts itself. Matches the backend message.
  title: "This account has no calibration deadline",
  body: "Their calibration is already complete, or it was never required, so there is no deadline to extend or reactivate.",
};

// 409 self_target_refused — branchable because it carries a dict `details.reason`
// even though its `error_code` is null.
const SELF_TARGET_REFUSED: AdminUserBanner = {
  variant: "warn",
  title: "You can't take this action on your own account",
  body: "Ask another administrator to make this change.",
};

// 409 calibration_deadline_write_conflict (DW-707) — two administrators raced for
// the same clinician's single `calibration_deadline_extensions` row and this one
// lost the lock. Branchable for the same reason `self_target_refused` is: the
// exception carries a dict `details.reason` even though its `error_code` is null.
//
// NOT collapsed into STALE_CONFLICT, whose copy hedges ("someone else MAY have
// updated this account") about an account that may be perfectly current. Here the
// server knows for certain what happened, and it is the one fact that makes the
// next action obvious — so the copy states it.
//
// The wording is deliberately NOT a refusal. Nothing about this administrator's
// authority or their input was rejected; they were simply second by milliseconds,
// and the same submission a moment earlier would have been written. Copy that
// reads like a denial would send a super_admin looking for a permission they
// already hold. `warn` + `refetch`, because the row on screen really is stale and
// reloading really is the remedy.
const CALIBRATION_DEADLINE_WRITE_CONFLICT: AdminUserBanner = {
  variant: "warn",
  title: "Someone else just changed this deadline",
  body: "Another administrator updated this clinician's calibration deadline while this dialog was open, so your change wasn't saved. Reload the record to see the deadline now in force, then decide whether you still need to change it.",
  refetch: true,
};

/**
 * ADMIN-011 (11.12) — an erasure precondition, or a reactivate against an already
 * erased account.
 *
 * The CODE is not the message here. One code carries five `details.reason`
 * values, and they call for four different next actions: disable the account
 * first, remove their seat, take the organization to CITS, or nothing at all
 * because the account is already gone. Collapsing them into one banner would
 * leave an admin with a refusal and no idea which of the four they were being
 * told about — so the reason is read and each is answered in its own words.
 *
 * `self_target_refused` deliberately reuses the existing uncoded-409 banner: it
 * is the same fact, and 11.5's disable answers it without a code while 11.12's
 * erase answers it with one. Two spellings of one refusal is not two refusals.
 */
function erasurePreconditionBanner(details: unknown): AdminUserBanner {
  switch (readReason(details)) {
    case "self_target_refused":
      return SELF_TARGET_REFUSED;
    case "account_not_disabled":
      return {
        variant: "warn",
        title: "Disable this account's access first",
        body: "An account can only be deleted after its access has been disabled. Use Disable access, then delete.",
      };
    case "holds_active_seat":
      return {
        variant: "warn",
        title: "This user still holds a seat",
        body: "Remove them from their organization's seats before deleting the account.",
      };
    // DW-235 — this used to say "transfer ownership … to another administrator",
    // and there is no such control: nothing in the platform reassigns an
    // organization's owner and nothing deletes an organization. An admin who
    // followed that sentence would search the console for a button that does not
    // exist and come back to this same refusal. Until ownership transfer is
    // built, the honest answer names the route that actually reaches someone who
    // can act, and promises no self-service path.
    case "owns_professional_organization":
      return {
        variant: "warn",
        title: "This user owns an organization",
        body: "There is no self-service way to move that ownership. Ask CITS to reassign the organization before this account can be deleted.",
      };
    case "account_erased":
      return {
        variant: "warn",
        title: "This account has already been deleted",
        body: "Deletion is permanent — there is no identity or password left to restore access to.",
      };
    default:
      // A reason this client has never heard of. Say only what is certainly
      // true, and hand over the code so support can look up the rest, rather
      // than guessing which of the five it resembles.
      return {
        variant: "warn",
        title: "This account can't be deleted right now",
        body: "The server refused the deletion. Refresh to see the current state of this account before retrying.",
        code: "ADMIN-011",
      };
  }
}

// The collapsed conflict family (see rule 2 in the header).
const STALE_CONFLICT: AdminUserBanner = {
  variant: "warn",
  title: "This user's state has changed",
  body: "Someone else may have updated this account. Refresh to see the current state before retrying.",
  refetch: true,
};

// ADMIN-008 (11.9) — an account with that email already exists. Handled INLINE on
// the email field; the other fields keep their values.
const DUPLICATE_EMAIL_MESSAGE = "An account with this email address already exists.";

// The limiter refusal. The CODE is rendered from the response and never from a
// literal here (11.9 boundary #7): ADMIN-009 was double-claimed by 11.2 and 11.12
// during authoring and re-allocated at chain-check, and a hardcoded string would
// have needed re-fixing on that day and on every day like it. The wait comes from
// the same `details.retry_after_s` reader every other limiter on the platform
// uses; when the server sends no usable number we say it without one rather than
// invent a duration.
function rateLimited(error: ApiError): AdminUserBanner {
  const seconds = readRetryAfterSeconds(error.details);
  return {
    variant: "info",
    title: "Too many actions in a short time",
    body: seconds
      ? `Please wait about ${formatWaitMinutes(seconds)} before trying again.`
      : "Please wait a little while before trying again.",
    code: hasServerCode(error) ? error.code : undefined,
  };
}

/**
 * True when the SERVER named the failure. `UNKNOWN` is `api-client`'s placeholder
 * for a response that carried no code at all, so it is not a code.
 *
 * The uncoded-403 guard and the default branch BOTH read this one predicate on
 * purpose: the guard's job is to let a coded 403 past the session banner, and it
 * is only an improvement if what it lets past reaches a branch that can actually
 * render the code. Two spellings of "has a code" could drift apart and drop a
 * coded 403 into the codeless generic instead.
 */
function hasServerCode(error: ApiError): boolean {
  return Boolean(error.code) && error.code !== "UNKNOWN";
}

/** Read a structured `reason` off an error whose `details` is a dict. */
function readReason(details: unknown): string | null {
  if (details && typeof details === "object" && !Array.isArray(details) && "reason" in details) {
    const reason = (details as { reason?: unknown }).reason;
    return typeof reason === "string" ? reason : null;
  }
  return null;
}

/** Read a numeric/string field off a dict `details` (ORG-014's cooldown fields). */
function readDetailValue(details: unknown, key: string): string | null {
  if (details && typeof details === "object" && !Array.isArray(details) && key in details) {
    const value = (details as Record<string, unknown>)[key];
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
  }
  return null;
}

/**
 * ORG-014 — the email-change cooldown. The wait is a fact only the server knows,
 * so the copy is BUILT from the structured `cooldown_hours` /
 * `cooldown_expires_at` fields rather than echoing the server's sentence.
 */
function cooldownBanner(details: unknown): AdminUserBanner {
  const hours = readDetailValue(details, "cooldown_hours");
  const expiresAt = readDetailValue(details, "cooldown_expires_at");
  const when = expiresAt
    ? ` You can try again after ${expiresAt}.`
    : hours
      ? ` You can try again in about ${hours} hours.`
      : "";
  return {
    variant: "info",
    title: "This account is in a cooldown period",
    body: `A recent change to this account is still settling, so the action was refused.${when}`,
  };
}

/** Translate one 422 detail item into our own (PHI-free) field message. */
function applyDetailItem(item: unknown, into: AdminUserFieldErrors): void {
  if (!item || typeof item !== "object") return;
  const loc = (item as { loc?: unknown }).loc;
  const field = Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : "";

  if (field === "first_name" && !into.firstName) {
    into.firstName = "Enter the user's first name (1–255 characters).";
  } else if (field === "last_name" && !into.lastName) {
    into.lastName = "Enter the user's last name (1–255 characters).";
  } else if (field === "email" && !into.email) {
    into.email = "Enter a valid email address.";
  }
}

function parse422Details(details: unknown): AdminUserFieldErrors {
  const fieldErrors: AdminUserFieldErrors = {};
  if (Array.isArray(details)) {
    for (const item of details) applyDetailItem(item, fieldErrors);
  }
  return fieldErrors;
}

/** Map any admin-user failure to inline field errors + an optional banner. */
export function toAdminUserError(error: unknown): MappedAdminUserError {
  if (error instanceof ApiError) {
    // --- Coded branches first: a specific code always beats a bare status. ---
    if (error.code === "ADMIN-006") return { fieldErrors: {}, banner: INSUFFICIENT_SCOPE };
    if (error.code === "ADMIN-007") return { fieldErrors: {}, banner: OUTSIDE_SCOPE };
    if (error.code === "ADMIN-011") {
      return { fieldErrors: {}, banner: erasurePreconditionBanner(error.details) };
    }
    if (error.code === "ADMIN-012") return { fieldErrors: {}, banner: TARGET_NOT_ACTIVATED };
    if (error.code === "ADMIN-014") return { fieldErrors: {}, banner: TARGET_NOT_BLOCKED };
    if (error.code === "ADMIN-015") return { fieldErrors: {}, banner: TARGET_ALREADY_ACTIVE };
    // Story 16.7 AC15. Both sit with the coded branches for the reason ORG-017
    // records below: ADMIN-018 is a 409 and would otherwise land on
    // STALE_CONFLICT, telling the admin to refresh a row that is not stale — the
    // block is real and refreshing will never clear it.
    if (error.code === "ADMIN-017") return { fieldErrors: {}, banner: NO_COMPLETED_CHECKOUT };
    if (error.code === "ADMIN-018") {
      return { fieldErrors: {}, banner: TARGET_BLOCKED_NOT_PAYABLE };
    }
    // Story 20.3 AC16 + the PR #821 review — four codes, four banners, no shared
    // arm. See the block above each for why none of them may fall through to a
    // status branch.
    if (error.code === "ADMIN-019") {
      return { fieldErrors: {}, banner: CALIBRATION_DEADLINE_IN_PAST };
    }
    // ADMIN-022 sits between 019 and 020, the order its guards run on the server.
    if (error.code === "ADMIN-022") {
      return { fieldErrors: {}, banner: CALIBRATION_DEADLINE_SHORTENED };
    }
    if (error.code === "ADMIN-020") {
      return { fieldErrors: {}, banner: CALIBRATION_DEADLINE_UNCHANGED };
    }
    if (error.code === "ADMIN-021") {
      return { fieldErrors: {}, banner: TARGET_HAS_NO_CALIBRATION_DEADLINE };
    }
    if (error.code === "ORG-014") return { fieldErrors: {}, banner: cooldownBanner(error.details) };
    // ORG-017 sits with the coded branches, ahead of the 409 fall-through. Without
    // this line the refusal this story's own diff creates lands on STALE_CONFLICT,
    // which tells an org_admin the screen is out of date and to reload — advice
    // that is false and that they can follow forever.
    if (error.code === "ORG-017") return { fieldErrors: {}, banner: TARGET_BLOCKED };
    // Story 22.1 (AC24) — the attach refusals. See the block above each banner
    // for why none of them may fall through to a status branch.
    if (error.code === "ORG-009") return { fieldErrors: {}, banner: TARGET_NOT_A_CLINICIAN };
    if (error.code === "ORG-019") {
      return { fieldErrors: {}, banner: TARGET_ACCOUNT_RECORD_MISSING };
    }
    if (error.code === "ORG-023") return { fieldErrors: {}, banner: ORG_NO_REMAINING_SEATS };
    if (error.code === "ORG-024") {
      return { fieldErrors: {}, banner: contractNotInForceBanner(error.details) };
    }
    if (error.code === "ORG-025") return { fieldErrors: {}, banner: ORG_IS_PERSONAL };
    if (error.code === "ORG-026") return { fieldErrors: {}, banner: TARGET_ALREADY_SEATED };
    if (error.code === "ORG-027") return { fieldErrors: {}, banner: TARGET_ERASED };
    if (error.code === "ORG-028") {
      return { fieldErrors: {}, banner: TARGET_ALREADY_CERTIFIED };
    }
    // ORG-032 sits immediately after ORG-028, the order the server's own guards
    // run in — an `already_certified` target satisfies both predicates, and
    // ORG-028 is raised first so that cohort is never told to go and complete a
    // calibration CITS accepted them as not needing.
    if (error.code === "ORG-032") {
      return { fieldErrors: {}, banner: TARGET_NOT_CALIBRATED };
    }
    // ORG-033 sits with the other attach refusals and ahead of every status
    // branch. On the server it is raised BEFORE ORG-028/ORG-032 — a deactivated
    // account cannot sign in, so it cannot go and complete a calibration — but
    // the order here is presentational only: these branches key on the code that
    // actually arrived, and exactly one code ever does.
    if (error.code === "ORG-033") {
      return { fieldErrors: {}, banner: TARGET_INACTIVE };
    }
    // Story 22.3 (AC27) — the reversal refusals. ORG-027 above is shared with
    // the attach and needs no second branch.
    if (error.code === "ORG-029") {
      return { fieldErrors: {}, banner: NO_ACTIVE_ATTACHMENT };
    }
    if (error.code === "ORG-030") {
      return { fieldErrors: {}, banner: NOT_ATTACHMENT_CREATED };
    }
    if (error.code === "ORG-031") {
      return { fieldErrors: {}, banner: windowElapsedBanner(error.details) };
    }
    if (error.code === "ADMIN-008") {
      // Attach to the field the admin can actually fix; no banner (11.9 AC6).
      return { fieldErrors: { email: DUPLICATE_EMAIL_MESSAGE }, banner: null };
    }

    if (error.status === 422) {
      const fieldErrors = parse422Details(error.details);
      const handledInline = Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors,
        // Only banner when nothing could be attached to a field, so the admin is
        // never told "check the details" with no field actually flagged.
        banner: handledInline
          ? null
          : {
              variant: "warn",
              title: "Check the details",
              body: "Please review the fields and try again.",
            },
      };
    }

    if (error.status === 409) {
      // Uncoded 409s: `self_target_refused` and `calibration_deadline_write_conflict`
      // are distinguishable because they carry a dict `details.reason`. Every other
      // uncoded 409 in this family has a bare STRING `details` carrying the target's
      // email — indistinguishable without prose-matching, and unrenderable — so it
      // collapses to the stale-state banner rather than leaking the string or
      // guessing which conflict it was.
      if (readReason(error.details) === "self_target_refused") {
        return { fieldErrors: {}, banner: SELF_TARGET_REFUSED };
      }
      if (readReason(error.details) === "calibration_deadline_write_conflict") {
        return { fieldErrors: {}, banner: CALIBRATION_DEADLINE_WRITE_CONFLICT };
      }
      return { fieldErrors: {}, banner: STALE_CONFLICT };
    }

    if (error.status === 429) return { fieldErrors: {}, banner: rateLimited(error) };

    // 401 and an UNCODED 403 are the same user-visible situation (see the
    // SESSION_EXPIRED note), so only an uncoded 403 joins 401 here.
    //
    // A CODED 403 is a different situation and must NOT land here. Only the
    // codes enumerated above match by name; every other coded 403 — ADMIN-009
    // today, and any code the server gains before this file learns it — would
    // otherwise be reported as an expired session. That sends the admin to sign in
    // again over a refusal their session had nothing to do with, and the reason
    // the server did give never reaches them. They fall through to the default
    // branch instead, which names the server's own code.
    //
    // 401 stays unconditional: whatever code rides along, the request was not
    // authenticated, so "sign in again" is the true remedy.
    if (error.status === 401 || (error.status === 403 && !hasServerCode(error))) {
      return { fieldErrors: {}, banner: SESSION_EXPIRED };
    }

    // Default — surface the server's OWN code verbatim. No translation table, so
    // a code this story does not own (ADMIN-009 today) cannot be mis-explained.
    // Status 0 is a transport failure (`isOfflineError`): its "code" is the
    // client's own placeholder, and showing it as a support handle would send an
    // admin to quote a string the server never sent.
    if (error.status !== 0 && hasServerCode(error)) {
      return {
        fieldErrors: {},
        banner: { ...GENERIC, code: error.code },
      };
    }
  }
  // NETWORK_ERROR / AuthExpiredError / unknown — nothing internal leaked.
  return { fieldErrors: {}, banner: GENERIC };
}
